export type Serializer<T, Ser> = (value: T) => Ser;
export type Deserializer<T, Des> = (value: Des) => T;

export function getLocalStorage<T = unknown, Des = unknown>(path: string, deserializer?: Deserializer<T, Des>): T | undefined {
	try {
		const value = JSON.parse(localStorage.getItem(path)!);
		return deserializer ? deserializer(value) : value;
	} catch (_) {
		return undefined;
	}
}

export function setLocalStorage<T = unknown, Ser = unknown>(path: string, data: T, serializer?: Serializer<T, Ser>): void {
	localStorage.setItem(path, JSON.stringify(serializer ? serializer(data) : data));
}

// The keys that survive the wholesale localStorage clears — the logout and
// the superadmin login reset: device-level preferences and the remembered
// import mappings, as opposed to per-session data.
const PRESERVED_STORAGE_KEYS = [
	'language',
	'currency',
	'companies_is_default_hidden_columns',
	'companies_hidden_columns',
	'promotions_import_mapping',
	'products_import_mapping',
];

export function clearStoragePreservingPreferences(): void {
	const preserved: [string, string][] = [];
	for (const key of PRESERVED_STORAGE_KEYS) {
		const value = localStorage.getItem(key);
		if (value !== null) preserved.push([key, value]);
	}
	localStorage.clear();
	for (const [key, value] of preserved) localStorage.setItem(key, value);
}

export const DEFAULT_PARAMS = {
	page: 1,
	limit: 50,
	sort: 'asc',
	by: 'name'
};

// Read the listing 'params' from localStorage. The key is absent after a
// logout / superadmin login clear or on a fresh browser, so always fall back
// to the defaults instead of returning null.
export function getParams(): Record<string, any> {
	const params = getLocalStorage<Record<string, unknown>>('params');
	return params && typeof params === 'object' ? params : { ...DEFAULT_PARAMS };
}

// reset localStorage Params
export const resetParams = () => {
	localStorage.removeItem('typeView');
	localStorage.setItem('params', JSON.stringify(DEFAULT_PARAMS));
	localStorage.setItem('mapParams', JSON.stringify({}));
};
